import { useEffect, useState, useRef } from "react";
import { api, initials } from "../lib/api";
import { Send, AtSign, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { subscribeWS } from "../lib/ws";

/**
 * Reusable comments thread for any entity.
 * Props:
 *  - entityType: "task" | "document" | "field_entry" | "project" | "form" | "mission"
 *  - entityId: string
 *  - users: array of {id, email, full_name} (for @ mention picker)
 */
export default function CommentThread({ entityType, entityId, users = [] }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [total, setTotal] = useState(0);
  const [nextBefore, setNextBefore] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionFilter, setMentionFilter] = useState("");
  const taRef = useRef(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/comments?entity_type=${entityType}&entity_id=${entityId}&limit=20`);
      setComments(r.data.items || []);
      setTotal(r.data.total ?? (r.data.items?.length || 0));
      setNextBefore(r.data.next_before || null);
    } finally { setLoading(false); }
  };

  const loadOlder = async () => {
    if (!nextBefore || loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await api.get(`/comments?entity_type=${entityType}&entity_id=${entityId}&limit=20&before=${encodeURIComponent(nextBefore)}`);
      setComments((prev) => [...(r.data.items || []), ...prev]);
      setNextBefore(r.data.next_before || null);
      // Update total if backend includes it (it won't on subsequent pages — keep current)
      if (typeof r.data.total === "number") setTotal(r.data.total);
    } finally { setLoadingMore(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [entityType, entityId]);

  // Real-time: on comment_added for our entity, refresh
  useEffect(() => {
    const unsub = subscribeWS((msg) => {
      if (msg.type === "comment_added" && msg.payload?.entity_type === entityType && msg.payload?.entity_id === entityId) {
        refresh();
      }
    });
    return unsub;
    // eslint-disable-next-line
  }, [entityType, entityId]);

  const onTextChange = (e) => {
    const v = e.target.value;
    setText(v);
    // Detect "@" trigger: find last @ that starts a token
    const cursor = e.target.selectionStart;
    const before = v.slice(0, cursor);
    const m = before.match(/(^|\s)@([\w.+-]*)$/);
    if (m) { setMentionOpen(true); setMentionFilter(m[2].toLowerCase()); }
    else { setMentionOpen(false); setMentionFilter(""); }
  };

  const insertMention = (email) => {
    if (!taRef.current) return;
    const cursor = taRef.current.selectionStart;
    const before = text.slice(0, cursor);
    const after = text.slice(cursor);
    const replaced = before.replace(/(^|\s)@([\w.+-]*)$/, (_m, sp) => `${sp}@${email} `);
    const next = replaced + after;
    setText(next);
    setMentionOpen(false);
    setTimeout(() => taRef.current?.focus(), 30);
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!text.trim()) return;
    setPosting(true);
    try {
      await api.post("/comments", { entity_type: entityType, entity_id: entityId, content: text });
      setText("");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setPosting(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer ce commentaire ?")) return;
    try { await api.delete(`/comments/${id}`); refresh(); }
    catch { toast.error("Non autorisé"); }
  };

  const filteredUsers = users.filter((u) => u.role !== "client" && (u.email?.toLowerCase().includes(mentionFilter) || u.full_name?.toLowerCase().includes(mentionFilter))).slice(0, 6);

  return (
    <div className="space-y-3" data-testid={`comments-${entityType}-${entityId}`}>
      <div className="flex items-center gap-2">
        <h4 className="font-heading text-sm font-bold text-slate-700">Commentaires ({total})</h4>
        {loading && <Loader2 size={12} className="animate-spin text-slate-400" />}
      </div>

      <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
        {nextBefore && (
          <button onClick={loadOlder} disabled={loadingMore}
            className="w-full text-xs text-btp-navy font-semibold hover:bg-slate-50 py-1.5 rounded flex items-center justify-center gap-1 disabled:opacity-50"
            data-testid={`load-older-${entityType}-${entityId}`}>
            {loadingMore ? <Loader2 size={11} className="animate-spin" /> : null}
            Voir messages plus anciens
          </button>
        )}
        {comments.length === 0 && !loading && <div className="text-xs text-slate-400 italic">Aucun commentaire — soyez le premier.</div>}
        {comments.map((c) => (
          <div key={c.id} className="flex gap-2 group" data-testid={`comment-${c.id}`}>
            <div className="w-7 h-7 rounded-full bg-btp-navy text-white flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
              {initials(c.author_name || c.user_email)}
            </div>
            <div className="flex-1 min-w-0 bg-slate-50 border border-slate-200 rounded px-3 py-2">
              <div className="flex items-baseline justify-between gap-2">
                <div className="text-xs font-semibold text-slate-800">{c.author_name || c.user_email}</div>
                <div className="text-[10px] font-mono text-slate-400">{new Date(c.created_at).toLocaleString("fr-FR")}</div>
              </div>
              <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap break-words">{renderContent(c.content)}</div>
            </div>
            {(user?.id === c.user_id || ["admin","chef_projet"].includes(user?.role)) && (
              <button onClick={() => onDelete(c.id)} className="text-slate-300 hover:text-red-600 opacity-0 group-hover:opacity-100" data-testid={`delete-comment-${c.id}`}>
                <Trash2 size={12} />
              </button>
            )}
          </div>
        ))}
      </div>

      <form onSubmit={submit} className="relative">
        <textarea
          ref={taRef}
          value={text}
          onChange={onTextChange}
          placeholder="Ajouter un commentaire… Tapez @ pour mentionner"
          rows={2}
          className="w-full text-sm px-3 py-2 border border-slate-300 rounded resize-none focus:border-btp-navy outline-none"
          data-testid={`comment-input-${entityType}-${entityId}`}
        />
        {mentionOpen && filteredUsers.length > 0 && (
          <div className="absolute bottom-full left-0 mb-1 bg-white border border-slate-200 rounded shadow-lg w-72 max-h-56 overflow-y-auto z-30" data-testid="mention-picker">
            {filteredUsers.map((u) => (
              <button type="button" key={u.id} onMouseDown={(e) => { e.preventDefault(); insertMention(u.email); }}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 border-b border-slate-100 last:border-0">
                <div className="w-7 h-7 rounded-full bg-btp-navy text-white flex items-center justify-center text-[10px] font-semibold">{initials(u.full_name)}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{u.full_name}</div>
                  <div className="text-xs text-slate-500 font-mono truncate">{u.email}</div>
                </div>
                <AtSign size={12} className="text-slate-400" />
              </button>
            ))}
          </div>
        )}
        <div className="flex justify-end mt-2">
          <button type="submit" disabled={posting || !text.trim()}
            className="bg-btp-navy hover:bg-btp-navy-hover disabled:opacity-50 text-white px-3 h-9 rounded text-sm font-semibold flex items-center gap-2"
            data-testid={`comment-submit-${entityType}-${entityId}`}>
            {posting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Publier
          </button>
        </div>
      </form>
    </div>
  );
}

// Render @mentions as highlighted spans
function renderContent(content) {
  const parts = [];
  const re = /@([\w.+-]+@[\w.-]+\.\w+)/g;
  let last = 0; let m;
  while ((m = re.exec(content)) !== null) {
    if (m.index > last) parts.push(content.slice(last, m.index));
    parts.push(<span key={m.index} className="text-btp-terra font-semibold bg-btp-terra/10 px-1 rounded">@{m[1]}</span>);
    last = m.index + m[0].length;
  }
  if (last < content.length) parts.push(content.slice(last));
  return parts;
}
